export interface EnvironmentKeys {
  infinite_watch_organization_id: string;
  split_io_api_key: string;
  app_env: string;
  segment_api_key: string;
  google_maps_api_key: string;
  userpilot_api_key: string;
  react_app_dd_client_id: string;
  react_app_dd_app_id: string;
  hubspot_portal_id: string;
  hubspot_smb_id: string;
  hubspot_enterprise_id: string;
  hubspot_broker_id: string;
  access_token: string;
  auth_cookie_domain: string;
  backend: string;
  login: string;
  login_api: string;
  legacyLLPEndpoint: string;
  hubspot_form_api_url: string;
  deals_dashboard_service_url: string;
  claims_service_url: string;
  modern_backend: string;
}

type WindowWithEnv = typeof globalThis & { env?: EnvironmentKeys };

export default function getEnv(): EnvironmentKeys {
  const runtime = globalThis as WindowWithEnv;
  const app_env = resolveAppEnv(runtime.env?.app_env);
  const isDevelopment = import.meta.env.MODE === 'development';
  const accessToken = import.meta.env.APP_ACCESS_TOKEN ?? '';

  const overrideEnv = isDevelopment ? localApiUrls : {};

  const defaultEnv: EnvironmentKeys = {
    access_token: accessToken,
    auth_cookie_domain: buildAuthCookieDomain(app_env),
    app_env,
    segment_api_key: 'YNsXu6edVN1et8bUy4FPHqLIwvQZrLWG',
    infinite_watch_organization_id: '68d2690ed6268bc912e28eb4',
    google_maps_api_key: '',
    userpilot_api_key: 'STG-NX-c13b0c77',
    react_app_dd_client_id: 'pub9bfa67b0220d0f0bc7928a9f3d9719e3',
    react_app_dd_app_id: '97b1ebc2-c107-4de1-8de9-a6dea59b86f4',
    hubspot_portal_id: '51952684',
    hubspot_smb_id: '85aeb751-fa44-41a8-afd2-9bd43473a72e',
    hubspot_enterprise_id: 'eabfb19e-36fa-4024-9e59-ec7e2a947027',
    hubspot_broker_id: '8ed59aa8-e33e-476c-8343-79f4ae845d46',
    hubspot_form_api_url: 'https://api.hsforms.com/submissions/v3/integration/submit/',
    split_io_api_key: 'hg413jic7afkc4aq1squv9anfnq2e02g3aoe',
    login: buildEnvironmentUrl(app_env, 'login', '/'),
    login_api: buildEnvironmentUrl(app_env, 'login-api', '/'),
    legacyLLPEndpoint: buildEnvironmentUrl(app_env, '', '/v2/landlord'),
    deals_dashboard_service_url: buildEnvironmentUrl(app_env, 'deals-dashboard'),
    claims_service_url: buildEnvironmentUrl(app_env, 'claims-portal', '/'),
    backend: buildEnvironmentUrl(app_env, 'landlord-portal-backend', '/api/'),
    modern_backend: buildEnvironmentUrl(app_env, 'landlord-portal-backend', '/llp/'),
    ...overrideEnv,
  };

  runtime.env = { ...defaultEnv, ...runtime.env };

  return runtime.env;
}

const apiProxyPaths = {
  backend: '/backend-api',
  modern_backend: '/modern-backend-api',
  deals_dashboard_service_url: '/dd-api',
  claims_service_url: '/claims-api',
  login: '/login-endpoint',
  login_api: '/login-api',
  legacyLLPEndpoint: '/legacy-llp-endpoint',
  hubspot_form_api_url: '/hubspot-api',
} as const satisfies Partial<Record<keyof EnvironmentKeys, string>>;

const localApiUrls: Partial<EnvironmentKeys> = Object.fromEntries(
  Object.entries(apiProxyPaths).map(([key, proxyPath]) => [key, `${proxyPath}/`]),
);

type Environment = 'development' | 'integration' | 'staging' | 'production';

const isValidEnvironment = (value: unknown): value is Environment => {
  const validEnvironments = ['development', 'integration', 'staging', 'production'] as const;
  return validEnvironments.includes(value as Environment);
};

const resolveAppEnv = (injected: unknown): Environment => {
  if (isValidEnvironment(injected)) {
    return injected;
  }

  if (isValidEnvironment(import.meta.env.APP_ENV)) {
    return import.meta.env.APP_ENV;
  }

  return 'integration';
};

/**
 * Domain the auth cookie is scoped to, mirroring the AUTH_COOKIE_DOMAIN that
 * tg-login-ui writes. Both apps must agree exactly: a mismatch produces two
 * `auth_tokens` cookies that the browser sends together and neither app can
 * tell apart.
 */
export const buildAuthCookieDomain = (environment: string) =>
  environment === 'production' ? 'theguarantors.com' : `${environment}.theguarantors.com`;

// Environment-specific URL builders
export const buildEnvironmentUrl = (environment: string, service: string, path: string = '') => {
  const env = environment === 'production' ? '' : `${environment}.`;
  const serviceUrl = service ? `${service}.` : '';

  return `https://${serviceUrl}${env}theguarantors.com${path}`;
};
